import { useSnowplowContext } from "@simplybusiness/services";
import type { ReactElement, ReactNode } from "react";
import { cloneElement, useCallback, useEffect, useRef, useState } from "react";
import { useInterventions } from "../../contexts/InterventionsProvider";
import type { InterventionMessage } from "../../types";

type InterventionWrapperProps = {
  name: string;
  children: ReactNode;
};

export const InterventionWrapper = ({
  name,
  children,
}: InterventionWrapperProps) => {
  const { eventEmitter, duid, site, vertical, setShown } = useInterventions();
  const hasSentShownEvent = useRef(false);
  const [interventionData, setInterventionData] =
    useState<InterventionMessage | null>(null);
  const { snowplow } = useSnowplowContext();
  const [show, setShow] = useState(false);

  const onDismiss = () => {
    setShow(false);
    eventEmitter?.off(name);
  };

  // Inject snowplow instance and name into the event factory
  const fireEvent = useCallback(
    (action: string) =>
      snowplow?.trigger("interventionInteractionCompleted", {
        interventionName: name,
        userUniqueIdentifier: duid,
        userUniqueIdentifierType: "duid",
        action,
        site,
      }),
    [duid, name, site, snowplow],
  );

  // Subscribe to events for the given intervention name
  useEffect(() => {
    eventEmitter?.on(name, data => {
      if (!duid) return;
      setInterventionData(data);
      if (data.show) {
        setShow(true);
        // Send shown state to IDM server
        if (!hasSentShownEvent.current) {
          setShown(data.intervention_id)
            .then(() => {
              hasSentShownEvent.current = true;
            })
            .catch(console.error);
        }
      }
    });
    return () => eventEmitter?.off(name);
  }, [eventEmitter, name, setShown, duid]);

  if (!show || !duid) {
    return null;
  }

  // Pass intervention data and event handlers to the child component
  return cloneElement(
    children as ReactElement,
    {
      data: interventionData,
      onDismiss,
      fireEvent,
      duid, // Not needed in React SSR pages, but required for HAML versions
      vertical,
    } as {
      data?: InterventionMessage | null;
      onDismiss?: () => void;
      fireEvent?: (action: string) => unknown;
      duid?: string;
      vertical?: string;
    },
  );
};
